import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Table primitives — rc59 "Linear/Terminal" compact refresh.
 *
 * Cambios respecto a rc≤58:
 *   · TableHead: tipografía JetBrains Mono (más técnica, consistente con el
 *     resto del sistema Onyx Electric). Mantengo uppercase tracking-wider y
 *     altura h-8 (~32px).
 *   · TableRow: agregado `border-l-2 border-l-transparent` con
 *     `hover:border-l-electric hover:bg-electric/[0.03]` — al pasar el mouse
 *     cada row muestra un acento azul a la izquierda (estilo Linear/Notion).
 *     El bg de hover es ultra-sutil (3% electric) para no robar atención.
 *   · TableCell: padding reducido `py-1.5` (era py-2). Filas más densas, +/-30px.
 *   · TableHeader: bg reducido (de muted/30 → muted/20) para que el contraste
 *     con las rows sea más limpio.
 *
 * Compatibilidad: ningún caller necesita cambios. Las clases `className` que
 * pasen siguen mergeándose vía cn().
 */
export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="relative w-full overflow-auto">
      <table className={cn("w-full caption-bottom text-xs", className)} {...props} />
    </div>
  );
}

export function TableHeader({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead className={cn("[&_tr]:border-b [&_tr]:border-l-0 bg-muted/20", className)} {...props} />
  );
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn("[&_tr:last-child]:border-0", className)} {...props} />;
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn(
        // rc59: border-l accent al hover (Linear-style). transparent → electric.
        "border-b border-l-2 border-l-transparent transition-colors",
        "hover:bg-electric/[0.03] hover:border-l-electric",
        "data-[state=selected]:bg-electric/[0.06] data-[state=selected]:border-l-electric",
        className,
      )}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        // rc59: font-mono — header se ve más técnico, alinea con folios/montos abajo.
        "h-8 px-3 text-left align-middle font-mono text-[10px] font-semibold uppercase tracking-wider text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      // rc59: py-1.5 (más denso). Compatible: callers pueden seguir overrideando.
      className={cn("px-3 py-1.5 align-middle text-xs", className)}
      {...props}
    />
  );
}

export function TableEmpty({ children }: { children: React.ReactNode }) {
  return (
    <TableRow>
      <td colSpan={100} className="py-12 text-center text-xs text-muted-foreground">
        {children}
      </td>
    </TableRow>
  );
}
